import { Node, mergeAttributes } from '@tiptap/core';
import { Fragment, type Node as ProseMirrorNode, type Mark, Slice, Schema, NodeType } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import { SvelteNodeViewRenderer } from 'svelte-tiptap';
import { dropPoint } from '@tiptap/pm/transform';

import BhsnBlockReferenceComponent from './BhsnBlockReferenceComponent.svelte';
import { BhsnReferenceExtension } from './BhsnReferenceExtension';
import { findParentByNodeTypeName } from '../editorUtils';
import { dev } from '$app/environment';
import { differenceWith, intersectionWith } from 'lodash-es';

// blockReferenceNode가 갖는 attr. Node.create호출시 제네릭이나 기타방법으로 전달 불가하여 문서화를 위해 타입정의
export type BhsnBlockReferenceNodeAttributes = {
    'data-id': string;
    'data-name': string;
    'data-default-slot-id': string;
    'data-removable': boolean;
    'focused-input': boolean;
};

export type BhsnBlockReferenceItem = {
    id: string;
    name: string;
    content: Fragment;
    size: number;
    text: string;
};

export interface BhsnReferenceOptions {
    HTMLAttributes: Record<string, unknown>;
}
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        blockReference: {
            updateBlockReferenceContent: (attributes: Array<{ id: string; value?: string | ProseMirrorNode[] }>) => ReturnType;
            updateBlockReferenceContentInOrder: (attributes: Array<{ id: string; value?: string | ProseMirrorNode[] }>) => ReturnType;
            updateSlotsInBlockReference: (id: string, slotAttributes: Array<{ id: string; label: string }>) => ReturnType;
            focusedInputBlockReferenceContent: (id: string, scrollInto?: boolean, scrollIntoOption?: ScrollIntoViewOptions) => ReturnType;
            scrollIntoFocusedInputBlockReferenceContent: (id: string, option?: ScrollIntoViewOptions) => ReturnType;
            blurInputBlockReferenceContent: () => ReturnType;
            removeBlockReferences: (ids: string[]) => ReturnType;
            mapBlockReferenceAttributes: (mapper: (node: ProseMirrorNode) => ProseMirrorNode['attrs'] | null) => ReturnType;
        };
    }
}

export const BhsnBlockReferenceExtension = Node.create<BhsnReferenceOptions>({
    name: 'blockReference',
    group: 'block',
    content: '(slotReference)*',
    defining: true,
    isolating: true,
    inline: false,
    draggable: true,

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },
    addAttributes() {
        return {
            'data-id': {
                default: '',
            },
            'data-name': {
                default: '',
            },
            'data-default-slot-id': {
                default: '',
            },
            'focused-input': {
                default: undefined,
            },
            'data-removable': {
                default: false,
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'bhsn-block-reference',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['bhsn-block-reference', mergeAttributes(HTMLAttributes), 0];
    },

    addCommands() {
        return {
            updateBlockReferenceContent:
                (attributes: Array<{ id: string; value?: string | ProseMirrorNode[] }>) =>
                ({ tr, state, dispatch }) => {
                    const { doc, schema } = state;
                    const blockReferenceNodeType = schema.nodes.blockReference;

                    const nodeInfos = findNodesToUpdate(doc, blockReferenceNodeType, attributes);

                    if (dev && nodeInfos.length === 0) {
                        console.warn(`updateBlockReferenceContent: No block reference nodes found for ${JSON.stringify(attributes)}`);
                        return false;
                    }
                    for (let i = nodeInfos.length - 1; i >= 0; i--) {
                        const { node, pos } = nodeInfos[i];
                        const { value } = attributes.find(attr => attr.id === node.attrs['data-id'] || attr.id === node.attrs['data-name']) || {};

                        const fragment = createFragment(schema, value, node.marks);
                        const updatedNode = node.copy(fragment);
                        tr.replaceWith(pos, pos + node.nodeSize, updatedNode);
                    }

                    tr.setMeta('changedBhsnReference', true);
                    if (dispatch) dispatch(tr);
                    return true;
                },
            updateBlockReferenceContentInOrder:
                (attributes: Array<{ id: string; value?: string | ProseMirrorNode[] }>) =>
                ({ tr, state, dispatch }) => {
                    const { doc, schema } = state;
                    const blockReferenceNodeType = schema.nodes.blockReference;

                    const nodeInfos = findNodesToUpdate(doc, blockReferenceNodeType, attributes);

                    // attributes 배열의 길이와 nodeInfos 배열의 길이가 다르면 처리하지 않음
                    if (nodeInfos.length !== attributes.length) {
                        console.warn('Node count mismatch: attributes and nodeInfos length are different');
                        return false;
                    }

                    for (let i = nodeInfos.length - 1; i >= 0; i--) {
                        const { node, pos } = nodeInfos[i];
                        const { value } = attributes[i];

                        // value가 없으면 해당 노드는 건너뜀
                        if (!value) continue;

                        const fragment = createFragment(schema, value, node.marks);
                        const updatedNode = node.copy(fragment);
                        tr.replaceWith(pos, pos + node.nodeSize, updatedNode);
                    }

                    tr.setMeta('changedBhsnReference', true);
                    if (dispatch) dispatch(tr);
                    return true;
                },

            updateSlotsInBlockReference:
                (id: string, slotAttributes: Array<{ id: string; label: string }>) =>
                ({ tr, state, editor, dispatch }) => {
                    const existingSlots = editor.$nodes('slotReference');
                    if (!existingSlots?.length) return false;

                    const blockReferenceNodes = editor.$nodes('blockReference', { 'data-id': id });

                    if (!blockReferenceNodes?.length) return false;
                    const slotsToAdd = differenceWith(slotAttributes, existingSlots, (a, b) => a.id === b.attributes['data-id']);
                    const slotsToDelete = differenceWith(existingSlots, slotAttributes, (a, b) => a.attributes['data-id'] === b.id);
                    const slotsToUpdate = intersectionWith(existingSlots, slotAttributes, (a, b) => a.attributes['data-id'] === b.id);

                    slotsToUpdate.reverse().forEach(slot => {
                        const slotToUpdate = slotAttributes.find(attr => attr.id === slot.attributes['data-id']);

                        if (slotToUpdate) {
                            state.doc.descendants((node, pos) => {
                                if (node.type === state.schema.nodes.slotReference && node.attrs['data-id'] === slotToUpdate.id) {
                                    tr.setNodeMarkup(pos, null, { ...node.attrs, 'data-name': slotToUpdate.label });
                                    if (dispatch) dispatch(tr);
                                }
                            });
                        }
                    });

                    blockReferenceNodes.reverse().forEach(block => {
                        const newSlots = slotsToAdd.map(attr => editor.state.schema.nodes.slotReference.create({ 'data-id': attr.id, 'data-name': attr.label }, Fragment.from([])));
                        tr.insert(block.lastChild ? block.lastChild.pos + 1 : block.to - 2, Fragment.from(newSlots));
                    });

                    slotsToDelete.reverse().forEach(slot => {
                        tr.delete(slot.from - slot.node.nodeSize, slot.to - slot.node.nodeSize);
                    });

                    if (dispatch) dispatch(tr);
                    return true;
                },
            // Input 영역에서 data-id에 해당하는 block reference를 focus했을때 기존 focused-input를 모두 제거하고 해당 reference 에 focused-input attr을 추가합니다.
            focusedInputBlockReferenceContent:
                (id, scrollInto = false, scrollIntoOption?: ScrollIntoViewOptions) =>
                ({ tr, view, state, dispatch }) => {
                    const { doc, schema } = state;
                    const blockReferenceNodeType = schema.nodes.blockReference;
                    let blockReferenceNodePosition: number | null = null;

                    // 선택한 reference 노드에만 'focused-input' 속성을 추가합니다.
                    doc.descendants((node, pos) => {
                        if (node.type === blockReferenceNodeType) {
                            const { attrs } = node;
                            if (id && id === node.attrs['data-id']) {
                                tr.setNodeMarkup(pos, null, {
                                    ...attrs,
                                    'focused-input': true,
                                });
                                if (blockReferenceNodePosition === null) blockReferenceNodePosition = pos;
                            } else {
                                tr.setNodeMarkup(pos, null, {
                                    ...attrs,
                                    'focused-input': undefined,
                                });
                            }
                        }
                    });

                    if (scrollInto && blockReferenceNodePosition !== null) {
                        const referenceNodeView = view.nodeDOM(blockReferenceNodePosition);
                        if (referenceNodeView) {
                            setTimeout(() => {
                                (referenceNodeView as Element).scrollIntoView(scrollIntoOption || { behavior: 'smooth', block: 'center' });
                            });
                        }
                    }
                    if (dispatch) dispatch(tr);
                    return true;
                },
            // Input 영역에서 data-id에 해당하는 block reference를 focus했을때 해당 reference로 스크롤합니다.
            scrollIntoFocusedInputBlockReferenceContent:
                (id, option = { behavior: 'smooth', block: 'center' }) =>
                ({ view, state }) => {
                    if (!id) return false;
                    const { doc, schema } = state;
                    const blockReferenceNodeType = schema.nodes.blockReference;
                    let blockReferenceNodePosition: number | null = null;

                    doc.descendants((node, pos) => {
                        if (!blockReferenceNodePosition && node.type === blockReferenceNodeType && id === node.attrs['data-id']) {
                            blockReferenceNodePosition = pos;
                            return false;
                        }
                    });

                    if (blockReferenceNodePosition !== null) {
                        const referenceNodeView = view.nodeDOM(blockReferenceNodePosition);

                        if (referenceNodeView) {
                            setTimeout(() => {
                                (referenceNodeView as Element).scrollIntoView(option);
                            });
                        }
                    }
                    return true;
                },
            // Input 영역에서 data-id에 해당하는 reference를 blur했을때 기존 focused-input를 모두 제거
            blurInputBlockReferenceContent:
                () =>
                ({ tr, state, dispatch }) => {
                    const { doc, schema } = state;
                    const blockReferenceNodeType = schema.nodes.blockReference;

                    // 'focused-input' 속성을 모두 제거합니다.
                    doc.descendants((node, pos) => {
                        if (node.type === blockReferenceNodeType) {
                            const { attrs } = node;
                            const updatedAttrs = { ...attrs, 'focused-input': undefined };
                            tr.setNodeMarkup(pos, null, updatedAttrs);
                        }
                    });
                    if (dispatch) dispatch(tr);
                    return true;
                },

            removeBlockReferences:
                (ids: string[]) =>
                ({ editor, state, tr }) => {
                    const positions: Array<[number, number]> = [];

                    editor.state.doc.descendants((node, pos) => {
                        if (node.type === state.schema.nodes.blockReference && ids.includes(node.attrs['data-id'])) {
                            positions.push([pos, node.nodeSize]);
                        }
                    });

                    positions.toReversed().forEach(([pos, size]) => tr.delete(pos, pos + size));

                    return true;
                },
            mapBlockReferenceAttributes:
                (mapper: (node: ProseMirrorNode) => ProseMirrorNode['attrs'] | null) =>
                ({ tr, state, dispatch }) => {
                    state.doc.descendants((node, pos) => {
                        if (node.type === state.schema.nodes.blockReference) {
                            const attributes = mapper(node);
                            if (attributes) {
                                tr.setNodeMarkup(pos, null, { ...node.attrs, ...attributes });
                                if (dispatch) dispatch(tr);
                            }
                        }
                    });
                    return true;
                },
        };
    },
    addNodeView() {
        return SvelteNodeViewRenderer(BhsnBlockReferenceComponent);
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                props: {
                    handleDrop: (view, event, slice, moved) => {
                        // 1. 기본 상수 정의
                        const STOP_DROP = true;
                        const ALLOW_DROP = false;

                        try {
                            const jsonData = event.dataTransfer?.getData('application/json');
                            if (!jsonData) return ALLOW_DROP; // 다른 Extension이 처리하도록 함

                            const content = JSON.parse(jsonData);

                            // block reference 드롭이 아닌 경우 무시 (reference 드롭 등)
                            if (!content?.options?.length) return ALLOW_DROP;

                            // 2. 드롭 위치 좌표 확인
                            const coordinates = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });

                            if (!coordinates) {
                                return STOP_DROP;
                            }

                            // 3. 드롭 위치의 노드 정보 확인
                            const pos = view.state.doc.resolve(coordinates.pos);
                            const isInsideBlockReference = findParentByNodeTypeName(pos, BhsnBlockReferenceExtension.name);
                            const isInsideReference = findParentByNodeTypeName(pos, BhsnReferenceExtension.name);
                            const isInsideSlotReference = findParentByNodeTypeName(pos, 'slotReference');

                            // 드롭 위치 제한 검사
                            const dropTargetNode = pos.parent;
                            if (dropTargetNode.type.name === 'reference') {
                                return STOP_DROP;
                            }
                            if (dropTargetNode.type.name === 'blockReference' || dropTargetNode.type.name === 'slotReference') {
                                return STOP_DROP;
                            }

                            // 4. 내부 드래그&드롭 처리 (moved === true)
                            if (moved) {
                                // 블록 내부로의 드래그는 금지
                                if (isInsideBlockReference || isInsideReference || isInsideSlotReference) {
                                    return STOP_DROP;
                                }
                                // 나머지 내부 이동은 기본 동작 사용
                                return ALLOW_DROP;
                            }

                            // 5. 외부 드래그&드롭 처리
                            if (!content?.id || !content?.name) {
                                return STOP_DROP;
                            }

                            // 6. slotReference 노드 생성
                            const slotNodes = content.options.map(option =>
                                view.state.schema.nodes.slotReference.create(
                                    {
                                        'data-id': option.id,
                                        'data-name': option.label,
                                    },
                                    Fragment.from(view.state.schema.nodes.paragraph.create()),
                                ),
                            );

                            // 7. blockReference 노드 생성
                            const blockNode = view.state.schema.nodes.blockReference.create(
                                {
                                    'data-id': content.id,
                                    'data-name': content.name,
                                    'data-default-slot-id': content.options[0]?.id,
                                    'data-removable': true,
                                },
                                Fragment.from(slotNodes),
                            );

                            // 8. dropPoint를 사용하여 적절한 드롭 위치 계산
                            const slice = new Slice(Fragment.from(blockNode), 0, 0);
                            const dropPos = dropPoint(view.state.doc, coordinates.pos, slice);
                            if (dropPos == null) return STOP_DROP;

                            // 9. 에디터에 노드 추가
                            const { tr } = view.state;
                            tr.replaceWith(dropPos, dropPos, blockNode);
                            view.dispatch(tr);

                            return STOP_DROP;
                        } catch (error) {
                            console.error('Drop handling error:', error);
                            return STOP_DROP;
                        }
                    },
                },
            }),
        ];
    },
});

// 헬퍼 함수들을 밖으로 분리
const findNodesToUpdate = (doc: ProseMirrorNode, blockReferenceNodeType: NodeType, attributes: Array<{ id: string }>) => {
    const nodeInfos: Array<{ node: ProseMirrorNode; pos: number }> = [];
    doc.descendants((node, pos) => {
        if (node.type === blockReferenceNodeType) {
            if (attributes.some(attr => attr.id === node.attrs['data-id'] || attr.id === node.attrs['data-name'])) {
                nodeInfos.push({ node, pos });
            }
        }
    });
    return nodeInfos;
};

const createFragment = (schema: Schema, value: string | ProseMirrorNode[] | undefined, marks: readonly Mark[]) => {
    const createNodesFromString = (schema: Schema, value: string, marks: readonly Mark[]) => {
        const textNode = schema.text(value, marks);
        return [schema.nodes.slotReference.create(null, Fragment.from(textNode))];
    };

    const createNodesFromArray = (schema: Schema, value: ProseMirrorNode[]) => {
        return value
            .map(rawNode => {
                if (rawNode && typeof rawNode === 'object' && 'type' in rawNode) return rawNode as ProseMirrorNode;
                try {
                    return schema.nodeFromJSON(rawNode);
                } catch (e) {
                    console.error('Failed to convert node:', e);
                    return schema.nodes.slotReference.create();
                }
            })
            .filter(Boolean);
    };

    try {
        if (!value) return Fragment.empty;

        const nodes = typeof value === 'string' ? createNodesFromString(schema, value, marks) : createNodesFromArray(schema, value);

        return Fragment.from(nodes);
    } catch (e) {
        console.error('Error creating fragment:', e);
        return Fragment.empty;
    }
};
